/**
 * `strategies` namespace — English canonical source.
 *
 * Owned by the Strategies surface: the list route, strategy cards, the preset
 * gallery, the import panel, the detail route (controls + config), and the run
 * decision-replay route. The **status vocabulary** blocks below (strategy status,
 * run state, run outcome, execution mode / route, decision phase) are canonical —
 * `meta.tsx` maps every enum value to one of these keys, so orders/approvals and
 * every other surface that renders the same enum must reuse this wording (never a
 * synonym) to stay consistent with the product glossary. Shared chrome strings
 * (loading/retry/cancel/done) live in `common.*` and are reused, not duplicated.
 *
 * Keys are dot-free camelCase; `{param}` marks an interpolation slot. `ko/strategies.ts`
 * mirrors this shape (a missing or misspelled key is a compile error).
 */
export const strategies = {
  // ── list route ──
  listEyebrow: "Automation",
  listTitle: "Strategies",
  listDescription: "Your trading strategies, their status, and per-strategy dry-run state.",
  listLoadError: "Could not load strategies.",
  filterAll: "All",
  emptyFiltered: "No strategies match this filter.",
  emptyTitle: "No strategies yet.",
  emptyHint: "Install a preset below, import an export, or build one in chat.",

  // ── strategy status (canonical) ──
  statusDraft: "Draft",
  statusActive: "Active",
  statusPaused: "Paused",
  statusHalted: "Halted",
  statusArchived: "Archived",

  // ── strategy card ──
  tradesCount: "{count} trades",
  winRate: "{rate} win",
  noWinsYet: "no wins yet",
  noTradesYet: "No trades yet",
  pnlTrendAria: "Realized P&L trend",

  // ── controls ──
  exportJson: "Export JSON",
  exporting: "Exporting…",
  editInChat: "Edit in chat",
  liveOrdersLabel: "Live orders",
  dryRunSimulated: "simulated",
  liveOrdersActive: "live now",
  dryRunActiveHint: "Currently dry-run — no orders are sent.",
  liveOrdersActiveHint:
    "Real orders are being submitted. High-value orders still route to approval.",
  toggleLiveOrders: "Toggle live orders",
  lifecycleActivate: "Activate",
  lifecyclePause: "Pause",
  lifecycleResume: "Resume",
  lifecycleHalt: "Halt",
  lifecycleReactivate: "Reactivate",
  lifecycleArchive: "Archive",
  lifecycleUnarchive: "Restore",
  reactivateConfirm:
    "A halt is usually caused by something — a risk violation, etc. Reactivate once you've confirmed the cause.",
  deleteTitle: "Delete strategy",
  deleteHint: "Permanently removes this strategy. Trade history is kept.",
  deleteActiveHint: "Pause or halt this strategy before deleting it.",
  deleteStrategy: "Delete",
  deleteConfirm: "Confirm delete",
  deleting: "Deleting…",

  // ── preset gallery ──
  presetGalleryTitle: "Preset gallery",
  presetGallerySubtitle: "Install a vetted strategy as a dry-run draft",
  presetsLoadError: "Could not load presets.",
  presetsEmpty: "No presets are bundled.",
  presetInstallFailed: "Install failed.",
  presetInstalling: "Installing…",
  presetInstall: "Install as draft",

  // ── import panel ──
  importOpen: "Import JSON",
  importTitle: "Import strategy",
  importSubtitle: "Paste or upload a strategy export envelope",
  importing: "Importing…",
  importAsDraft: "Import as draft",
  chooseFile: "Choose file…",
  importInvalidJson: "Not valid JSON.",
  importInvalidEnvelope: "Not a valid strategy export envelope.",
  importFailed: "Import failed.",

  // ── detail route ──
  strategyEyebrow: "Strategy",
  notFound: "Not found",
  detailLoadError: "This strategy could not be loaded.",
  backToStrategies: "← Back to strategies",
  detailEyebrow: "Strategies · {slug}",
  controlsTitle: "Controls",
  controlsSubtitle: "Lifecycle, dry-run guard, export",
  metaAccount: "Account",
  metaUnbound: "Unbound",
  metaBudget: "Budget",
  metaNextRun: "Next run",
  metaUpdated: "Updated",
  priceTitle: "Price",
  primarySymbol: "Primary symbol",
  noPrimarySymbol: "No resolvable primary symbol in the universe.",
  priceWithSymbol: "Price · {symbol}",
  dailyCandles: "Daily candles",

  // ── account binding (detail route) ──
  accountBindingTitle: "Bind account",
  accountBindingSubtitle: "Set the trading account and an optional budget.",
  tradingAccountLabel: "Trading account",
  budgetAmountLabel: "Budget (optional)",
  budgetCurrencyLabel: "Currency",
  budgetAmountInvalid: "Enter a valid amount (e.g. 1000000).",
  saveAccountBinding: "Save",
  savingAccountBinding: "Saving…",
  noAccountsBound: "No accounts yet — sync one from",
  accountBindingLockedHint: "Can't be changed while active — pause the strategy first.",

  // ── decision-replay route ──
  runEyebrowSingle: "Run",
  decisionReplay: "Decision replay",
  runLoadError: "This run could not be loaded.",
  backToStrategy: "← Back to strategy",
  runEyebrow: "Run · {tick}",
  runsEyebrow: "Runs · {slug}",
  decisionReplayDescription:
    "Step-by-step audit: indicators, condition trace, risk checks, AI verdict, and order intent.",
  strategyAction: "← Strategy",
  runSummary: "Run summary",
  metaRoute: "Route",
  metaScheduled: "Scheduled",
  metaStarted: "Started",
  metaFinished: "Finished",

  // ── run state (canonical; see meta.tsx RUN_STATE_KEY) ──
  runStateScheduled: "Scheduled",
  runStateEvaluating: "Evaluating",
  runStatePendingApproval: "Pending approval",
  runStatePendingAi: "Pending AI",
  runStateExecuting: "Executing",

  // ── run outcome (canonical; see meta.tsx OUTCOME_KEY) ──
  noSignal: "No signal",
  blockedRisk: "Blocked · risk",
  filled: "Filled",
  partialFill: "Partial fill",
  rejected: "Rejected",
  skipped: "Skipped",
  vetoed: "Vetoed",
  expired: "Expired",
  outcomeError: "Error",
  pending: "Pending",

  // ── execution mode / run route (canonical) ──
  modeAuto: "Auto",
  modeApproval: "Approval",
  modeAiAdvisor: "AI advisor",

  // ── decision phase (canonical) ──
  phaseEvaluate: "Evaluate",
  phaseRisk: "Risk",
  phaseRoute: "Route",
  phaseExecute: "Execute",
} as const;

export type StrategiesKey = keyof typeof strategies;
